import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CommonModule, formatDate} from '@angular/common';
import {DxDataGridComponent, DxFormComponent, DxTemplateDirective} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import {CustomStore} from 'devextreme-aspnet-data-nojquery';
import {FilterPanelComponent} from '../../../../../components/library/filter-panel/filter-panel.component';
import {FilterConfig, GridPageComponent} from '../../../../common/grid-page.component';
import {OrderAppointmentOverride, OrderHistoryGridViewDto, OrderHistoryField, OrderHistoryKind} from '@services';
import EnumUtils from '../../../../../shared/enum-utils';
import {DATE_TIME_FORMAT} from 'src/app/shared/constants';
import {UserAvatarEmailComponent} from 'src/app/components/utils/user-avatar-email/user-avatar-email.component';
import {PreProductionStatusPipe} from '../../../../../pipes/pre-production-status.pipe';
import {ProductionStatusPipe} from '../../../../../pipes/production-status.pipe';
import {OrderExceptionPipe} from '../../../../../pipes/order-exception.pipe';
import {OrderAppointmentStatusPipe} from '../../../../../pipes/order-appointment-status.pipe';
import {EasternTimePipe} from '../../../../../pipes/eastern-time.pipe';

@Component({
    selector: 'app-order-history',
    standalone: true,
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        DxFormComponent,
        FilterPanelComponent,
        UserAvatarEmailComponent,
        PreProductionStatusPipe,
        ProductionStatusPipe,
        OrderExceptionPipe,
        OrderAppointmentStatusPipe
    ],
    providers: [PreProductionStatusPipe, ProductionStatusPipe, OrderExceptionPipe, OrderAppointmentStatusPipe, EasternTimePipe],
    templateUrl: './order-history.component.html',
})
export class OrderHistoryComponent extends GridPageComponent<OrderHistoryGridViewDto> implements OnChanges {
    @Input() number: string | null = null;

    protected override exportName: string = 'Order History Report';
    protected offset: number = 155;
    public dateTimeFormat = DATE_TIME_FORMAT;
    dataSource: CustomStore | OrderHistoryGridViewDto[] = [];

    readonly Field = OrderHistoryField;
    readonly Kind = OrderHistoryKind;

    private preProductionStatusPipe = inject(PreProductionStatusPipe);
    private productionStatusPipe = inject(ProductionStatusPipe);
    private orderExceptionPipe = inject(OrderExceptionPipe);
    private orderAppointmentStatusPipe = inject(OrderAppointmentStatusPipe);
    private easternTimePipe = inject(EasternTimePipe);

    private readonly fieldLabels: Record<string, string> = {
        [OrderHistoryField.LogisticalGroup]: 'Logistical Group',
        [OrderHistoryField.DeliveryNumber]: 'Delivery Number',
        [OrderHistoryField.SalesOrganization]: 'Sales Organization',
        [OrderHistoryField.PreProductionStatus]: 'Pre-Production Status',
        [OrderHistoryField.DocumentDate]: 'Document Date',
        [OrderHistoryField.StagingArea]: 'Staging Area',
        [OrderHistoryField.OverallWmStatus]: 'Overall WM Status',
        [OrderHistoryField.ProductionStatus]: 'Production Status',
        [OrderHistoryField.PoNumber]: 'PO Number',
        [OrderHistoryField.ProductionItem]: 'Production Item',
        [OrderHistoryField.ReasonForRejection]: 'Reason for Rejection',
        [OrderHistoryField.Batch]: 'Batch',
        [OrderHistoryField.OrderQuantity]: 'Order Quantity',
        [OrderHistoryField.OrderConfirmedQuantity]: 'Order Confirmed Quantity',
        [OrderHistoryField.ActualQuantityDelivered]: 'Actual Quantity Delivered',
        [OrderHistoryField.DeliveryQuantity]: 'Delivery Quantity',
        [OrderHistoryField.WmActivityStatus]: 'WM Activity Status',
        [OrderHistoryField.OverallPickStatus]: 'Overall Pick Status',
        [OrderHistoryField.Exceptions]: 'Exception',
        [OrderHistoryField.RequestedDeliveryDate]: 'Requested Delivery Date',
        [OrderHistoryField.DeliveryAppointment]: 'Delivery Appointment',
        [OrderHistoryField.Loading]: 'Loading',
        [OrderHistoryField.DeliveryDateTime]: 'Delivery Date/Time',
        [OrderHistoryField.SourceLocationAppointmentDate]: 'Source Location Appointment Date',
        [OrderHistoryField.DestinationLocationAppointmentDate]: 'Destination Location Appointment Date',
        [OrderHistoryField.DestinationLocationAppointmentTimeZone]: 'Destination Location Appointment Time Zone',
        [OrderHistoryField.AppointmentStatus]: 'Appointment Status',
        [OrderHistoryField.Appointment]: 'Appointment',
        [OrderHistoryField.AppointmentOverride]: 'Appointment Override',
        [OrderHistoryField.Mdt]: 'MDT',
        [OrderHistoryField.DeliveryCreated]: 'Delivery Created',
        [OrderHistoryField.HeaderException]: 'Exception',
        [OrderHistoryField.EmptyTrailer]: 'Empty Trailer',
        [OrderHistoryField.Outbound]: 'Outbound',
        [OrderHistoryField.ShipToId]: 'Ship-To ID',
        [OrderHistoryField.ShipToName]: 'Ship-To Name',
        [OrderHistoryField.ShipToStreet]: 'Ship-To Street',
        [OrderHistoryField.ShipToStreetNumber]: 'Ship-To Street Number',
        [OrderHistoryField.ShipToCity]: 'Ship-To City',
        [OrderHistoryField.ShipToRegion]: 'Ship-To Region',
        [OrderHistoryField.ShipToPostalCode]: 'Ship-To Postal Code',
    };

    private readonly dateFields = new Set<OrderHistoryField>([
        OrderHistoryField.DocumentDate,
        OrderHistoryField.RequestedDeliveryDate,
        OrderHistoryField.DeliveryAppointment,
        OrderHistoryField.Loading,
        OrderHistoryField.DeliveryDateTime,
        OrderHistoryField.SourceLocationAppointmentDate,
        OrderHistoryField.DestinationLocationAppointmentDate,
        OrderHistoryField.Appointment,
        OrderHistoryField.Mdt,
        OrderHistoryField.DeliveryCreated,
    ]);

    fieldOptions = EnumUtils.getEnumOptions(OrderHistoryField, (v) => this.fieldLabel(v));

    protected override filterConfig: FilterConfig[] = [
        {field: 'employee.name', op: 'contains', customFilter: this.nameOrEmailFilter},
        {field: 'created', op: '>='},
        {field: 'typeLabel', op: 'contains'},
        {field: 'field', op: '='},
    ];

    constructor() {
        super();
        this.init(OrderHistoryGridViewDto);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['number']) {
            this.dataSource = this.dataSources.orderHistory(this.number);
        }
    }

    fieldLabel(field: OrderHistoryField | string | undefined | null): string {
        return field != null ? (this.fieldLabels[field] ?? String(field)) : '';
    }

    isPreProductionField(field: OrderHistoryField | undefined | null): boolean {
        return field === OrderHistoryField.PreProductionStatus;
    }

    isProductionField(field: OrderHistoryField | undefined | null): boolean {
        return field === OrderHistoryField.ProductionStatus;
    }

    isAppointmentStatusField(field: OrderHistoryField | undefined | null): boolean {
        return field === OrderHistoryField.AppointmentStatus;
    }

    isAppointmentOverrideField(field: OrderHistoryField | undefined | null): boolean {
        return field === OrderHistoryField.AppointmentOverride;
    }

    isExceptionField(field: OrderHistoryField | undefined | null): boolean {
        return field === OrderHistoryField.Exceptions || field === OrderHistoryField.HeaderException;
    }

    isDateField(field: OrderHistoryField | undefined | null): boolean {
        return field != null && this.dateFields.has(field);
    }

    exceptionList(value: string | undefined | null): string[] {
        return value ? value.split(',').filter((v) => !!v) : [];
    }

    appointmentOverrideLabel(value: string | undefined | null): string {
        switch (value) {
            case OrderAppointmentOverride.Ltl: return 'LTL Load';
            case OrderAppointmentOverride.Manual: return 'Manual Appointment';
            case OrderAppointmentOverride.None: return 'None';
            default: return value ?? 'None';
        }
    }

    preProductionBadgeClass = (value: any): string => EnumUtils.getPreProductionStatusBadgeClass(value);
    productionBadgeClass = (value: any): string => EnumUtils.getProductionStatusBadgeClass(value);
    appointmentStatusBadgeClass = (value: any): string => EnumUtils.getOrderAppointmentStatusBadgeClass(value);

    calculateChangeCellValue = (row: OrderHistoryGridViewDto): string => {
        if (row.kind === OrderHistoryKind.LineAdded) return 'Added';
        if (row.kind === OrderHistoryKind.LineRemoved) return 'Deleted';

        const oldText = this.isDateField(row.field)
            ? this.formatDateValue(row.field, row.oldValueDate)
            : this.displayValue(row.field, row.oldValue);
        const newText = this.isDateField(row.field)
            ? this.formatDateValue(row.field, row.newValueDate)
            : this.displayValue(row.field, row.newValue);
        return `${this.fieldLabel(row.field)}: ${oldText} → ${newText}`;
    };

    protected formatDateValue(field: OrderHistoryField | undefined, value: Date | undefined | null): string {
        if (!value) return 'None';

        if (field === OrderHistoryField.SourceLocationAppointmentDate
            || field === OrderHistoryField.Appointment
            || field === OrderHistoryField.Mdt)
            return `${this.easternTimePipe.transform(value)} ET`;

        if (field === OrderHistoryField.DestinationLocationAppointmentDate)
            return formatDate(value, this.dateTimeFormat, 'en-US', 'UTC');

        return formatDate(value, this.dateTimeFormat, 'en-US');
    }

    private displayValue(field: OrderHistoryField | undefined, value: string | undefined | null): string {
        if (!value) return 'None';
        if (this.isPreProductionField(field)) return this.preProductionStatusPipe.transform(value);
        if (this.isProductionField(field)) return this.productionStatusPipe.transform(value);
        if (this.isAppointmentStatusField(field)) return this.orderAppointmentStatusPipe.transform(value);
        if (this.isAppointmentOverrideField(field)) return this.appointmentOverrideLabel(value);
        if (this.isExceptionField(field)) {
            return this.exceptionList(value).map((e) => this.orderExceptionPipe.transform(e)).join(', ');
        }
        return value;
    }
}
